/**
 * Mise en forme des durées et des dates (design system, Rédaction) : français, espace
 * insécable entre le nombre et l'unité, virgule décimale.
 */
import { ESPACE_INSECABLE as E, nombre } from './outils';

const MINUTE = 60_000;
const HEURE = 60 * MINUTE;
const JOUR = 24 * HEURE;

/**
 * Durée avant le prochain passage, sur un bouton de notation : « 1 min », « 10 min »,
 * « 3 h », « 8 j », « 1,5 mois », « 2,3 ans ». Courte et insécable.
 */
export function dureeDeNotation(ms: number): string {
  const d = Math.max(0, ms);
  if (d < HEURE) return `${Math.max(1, Math.round(d / MINUTE))}${E}min`;
  if (d < JOUR) {
    const h = d / HEURE;
    return `${nombre(h < 10 ? Math.round(h * 10) / 10 : Math.round(h), 1)}${E}h`;
  }
  const jours = d / JOUR;
  if (jours < 30) return `${Math.round(jours)}${E}j`;
  if (jours < 365) return `${nombre(Math.round((jours / 30) * 10) / 10, 1)}${E}mois`;
  const ans = Math.round((jours / 365) * 10) / 10;
  return `${nombre(ans, 1)}${E}${ans >= 2 ? 'ans' : 'an'}`;
}

/** Durée en minutes, arrondie : « 46 min », « 1 h 05 », « 12 h ». */
export function dureeCourte(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  if (m < 60) return `${m}${E}min`;
  const h = Math.floor(m / 60);
  const reste = m % 60;
  return reste === 0 ? `${h}${E}h` : `${h}${E}h${E}${String(reste).padStart(2, '0')}`;
}

/** Heure : « 4 h », « 14 h 30 ». */
export function heure(date: Date): string {
  const m = date.getMinutes();
  return m === 0
    ? `${date.getHours()}${E}h`
    : `${date.getHours()}${E}h${E}${String(m).padStart(2, '0')}`;
}

const MOIS = [
  'janv.',
  'févr.',
  'mars',
  'avr.',
  'mai',
  'juin',
  'juil.',
  'août',
  'sept.',
  'oct.',
  'nov.',
  'déc.',
];

function memeJour(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Date courte : « 12 sept. », ou « 18 déc. 2025 » hors de l'année en cours. */
export function dateCourte(date: Date, maintenant = new Date()): string {
  const jour = `${date.getDate()}${E}${MOIS[date.getMonth()]}`;
  return date.getFullYear() === maintenant.getFullYear() ? jour : `${jour} ${date.getFullYear()}`;
}

/** Échéance : « dans 10 min », « aujourd’hui à 14 h », « demain à 4 h », « le 12 oct. à 4 h ». */
export function decrireEcheance(date: Date, maintenant = new Date()): string {
  const ecart = date.getTime() - maintenant.getTime();
  if (ecart <= MINUTE) return 'maintenant';
  if (ecart < HEURE) return `dans ${Math.round(ecart / MINUTE)}${E}min`;
  if (memeJour(date, maintenant)) return `aujourd’hui à ${heure(date)}`;
  const demain = new Date(maintenant);
  demain.setDate(demain.getDate() + 1);
  if (memeJour(date, demain)) return `demain à ${heure(date)}`;
  return `le ${dateCourte(date, maintenant)} à ${heure(date)}`;
}

/** Prochaine révision relative, pour un tableau : « dans 10 min », « demain », « dans 8 j ». */
export function prochaineRevision(date: Date, maintenant = new Date()): string {
  const ecart = date.getTime() - maintenant.getTime();
  if (ecart <= 0) return 'maintenant';
  if (ecart < HEURE) return `dans ${Math.max(1, Math.round(ecart / MINUTE))}${E}min`;
  if (memeJour(date, maintenant)) return `dans ${Math.round(ecart / HEURE)}${E}h`;
  const demain = new Date(maintenant);
  demain.setDate(demain.getDate() + 1);
  if (memeJour(date, demain)) return 'demain';
  return `dans ${dureeDeNotation(ecart)}`;
}
